import { Injectable } from '@angular/core';
import { DatePipe } from '@angular/common';
import { NzDrawerRef, NzDrawerService } from 'ng-zorro-antd/drawer';
import {
  DrawerParms,
  DrawerType,
  Prescription,
  IConsultation,
  ICustomerUser,
  ILabReq
} from '@care-portals/api-interfaces';
import { formTypes } from '@care-portals/data';
import { DrawerStateFacade } from '../../+store/drawer-state/drawer-state.facade';
import { DrugDetailComponent } from '../../components/common/drug-detail/drug-detail.component';
import { OrderCreateComponent } from '../../components/order/create-order/create-order.component';
import { FindOrderComponent } from '../../components/common/find-order/find-order.component';
import { FulfillmentComponent } from '../../components/common/fulfillment/fulfillment.component';
import { CustomerConsultationComponent } from '../../components/customer-consultation/customer-consultation.component';
import { AllConsultationsViewComponent } from '../../components/order/all-consultations-view/all-consultations-view.component';
import { CommonFacade } from '../../+store/common/common.facade';
import { LabRequisitionDetailComponent } from '../../components/customer-lab-requisitions/lab-requisition-detail/lab-requisition-detail.component';
import { LabRequisitionDetailV2Component } from '../../components/customer-lab-requisitions/lab-requisition-detail-v2/lab-requisition-detail-v2.component';
import { ChatMainComponent } from '../../components/chat-widget/main/chat-main.component';
import { ScheduleComponent } from '../../components/schedules';
import { InvoicePreviewComponent } from '../../components/invoices/invoice-preview/invoice-preview.component';
import { CustomerCreateComponent } from '../../components/customer-create/customer-create.component';
import { AppointmentDetailsComponent } from '../../components/appointment-details/appointment-details.component';
import { OrderDetailsComponent } from '../../components/order/order-details/order-details.component';
import { CreateConsultationComponent } from '../../components/common/create-consultation/create-consultation.component';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { PrescriptionDetailsComponent } from '../../components/prescriptions/prescription-details/prescription-details.component';
import { WatchlistManagerComponent } from '../../components/common/watchlist-manager/watchlist-manager.component';
import { PrescriptionPdfComponent } from '../../components/prescriptions/prescription-pdf/prescription-pdf.component';
import { CustomerActivityComponent } from '../../components/customer/customer-activity/customer-activity.component';
import { CreateActivityComponent } from '../../components/common/create-activity/create-activity.component';
import { CannedMessagesComponent } from '../../components/chat-widget/canned-messages/canned-messages.component';
import { OrderCreateV2Component } from '../../components/order/create-order-v2/create-order-v2.component';
import { AppFacade } from '../../+store/app/app.facade';
import { AdminUserFacade } from '../../+store/admin-user/admin-user.facade';
import { take } from 'rxjs/operators';
import { SubscriptionDetailsComponent } from '../../components/subscription/subscription-details/subscription-details.component';
import { CreateSubscriptionComponent } from '../../components/subscription/create-subscription/create-subscription.component';
import { CustomerPrescriptionsComponent } from 'src/app/components/customer-prescriptions/customer-prescriptions.component';

@Injectable({
  providedIn: 'root'
})
export class DrawerService {
  types: { [key: string]: string } = {};
  drawerRef;
  openDrawerRefs: { [key: string]: { ref: NzDrawerRef; index: number } } = {};
  constructor(
    private drawerService: NzDrawerService,
    private drawerFacade: DrawerStateFacade,
    private commonFacade: CommonFacade,
    private datePipe: DatePipe,
    private readonly notificationService: NzNotificationService,
    private readonly appFacade: AppFacade,
    private readonly adminUserFacade: AdminUserFacade
  ) {
    for (const [key, value] of Object.entries(formTypes)) {
      this.types[key] = value.name;
    }
  }

  getConsultationTitle(consultation: IConsultation) {
    return (
      this.types[consultation.type] +
      ' Consultation<br/>' +
      this.datePipe.transform(consultation.createdAt, 'medium') +
      '<br/>Created By: ' +
      this.getCreatedByName(consultation.createdBy)
    );
  }

  private getCreatedByName(createdBy?: string): string {
    if (!createdBy || createdBy === 'customer') {
      return 'Patient';
    }

    this.adminUserFacade.getAdminUsers({}, false);

    let name = createdBy;
    this.adminUserFacade.crmUsersEntities$.pipe(take(1)).subscribe(users => {
      const user = users?.[createdBy];
      if (user) {
        name = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
      }
    });
    return name;
  }

  getRxDetailTitle(rx: Prescription) {
    return 'RX details';
  }

  openDrawer(params: DrawerParms): NzDrawerRef {
    let drawerRef: NzDrawerRef = null;
    switch (params.name) {
      case DrawerType.CREATE_RX: {
        drawerRef = this.drawerService.create<
          PrescriptionDetailsComponent,
          {
            customerId: string;
            rx: Prescription;
            mode: 'edit' | 'create';
            orderId?: number;
            sendRx?: boolean;
          },
          string
        >({
          nzTitle: 'Create RX',
          nzWidth: '70vw',
          nzContent: PrescriptionDetailsComponent,
          nzContentParams: {
            mode: 'create',
            rx: params.props.rx,
            customerId: params.props.customerId,
            orderId: params.props?.orderId,
            sendRx: params.props?.sendRx
          },
          nzZIndex: 1000
        });
        break;
      }
      case DrawerType.EDIT_RX: {
        drawerRef = this.drawerService.create<
          PrescriptionDetailsComponent,
          { rx: Prescription; mode: 'edit' | 'create' },
          string
        >({
          nzTitle: 'Edit RX',
          nzWidth: '70vw',
          nzContent: PrescriptionDetailsComponent,
          nzContentParams: {
            mode: 'edit',
            rx: params.props.rx
          },
          nzZIndex: 1000
        });

        break;
      }
      case DrawerType.DRUG_DETAIL: {
        drawerRef = this.drawerService.create<
          DrugDetailComponent,
          { din?: string },
          string
        >({
          nzWidth: '70vw',
          nzContent: DrugDetailComponent,
          nzContentParams: {
            din: params.props.din
          },
          nzZIndex: 1000,
          nzClosable: false
        });

        break;
      }
      case DrawerType.CREATE_FULFILL: {
        drawerRef = this.drawerService.create<
          FulfillmentComponent,
          { rx: Prescription },
          string
        >({
          nzTitle: 'Create New Fulfillment',
          nzWidth: '70vw',
          nzContent: FulfillmentComponent,
          nzContentParams: {
            rx: params.props.rx
          },
          nzZIndex: 1000
        });

        break;
      }
      case DrawerType.ORDER_CREATE: {
        if (
          !params.props ||
          !params.props.customerId ||
          (params.props.mode === 'refill' && !params.props?.products)
        ) {
          this.notificationService.error(
            'Oops!',
            'Something went wrong, refresh and try again.'
          );
          console.error(
            'Order Create Drawer failed to open because params is missing required fields',
            params.props
          );
          return;
        }

        this.appFacade.selectedLayout$.pipe(take(1)).subscribe(layout => {
          // TODO test this
          const ecommerceEnabled = layout.ecommerceEnabled;

          drawerRef = this.drawerService.create<
            OrderCreateComponent | OrderCreateV2Component,
            {
              customerId: string;
              orderId?: number;
              mode?: 'refill' | 'create';
            },
            string
          >({
            nzWidth: '70vw',
            nzContent: ecommerceEnabled
              ? OrderCreateComponent
              : OrderCreateV2Component,
            nzContentParams: {
              customerId: params.props.customerId,
              products: params.props.products,
              mode: params.props.mode
            },
            nzZIndex: 1000,
            nzClosable: false
          });
        });

        break;
      }
      case DrawerType.CREATE_CONSULTATION: {
        drawerRef = this.drawerService.create<
          CreateConsultationComponent,
          { consultation: IConsultation; customerId: string },
          string
        >({
          // nzTitle: 'Create New Questionnaire',
          nzWidth: '70vw',
          nzContent: CreateConsultationComponent,
          nzClosable: false,
          nzContentParams: {
            consultation: params.props.consultation,
            customerId: params.props.customerId
          },
          nzZIndex: 1000
        });

        break;
      }
      case DrawerType.FIND_ORDER: {
        drawerRef = this.drawerService.create<
          FindOrderComponent,
          { customerId: string },
          string
        >({
          nzTitle: 'Find Order',
          nzWidth: '70vw',
          nzContent: FindOrderComponent,
          nzContentParams: {
            customerId: params.props.customerId
          },
          nzZIndex: 1000
        });

        break;
      }
      case DrawerType.OPEN_CONSULTATION: {
        drawerRef = this.drawerService.create<
          CustomerConsultationComponent,
          { consultation: IConsultation },
          string
        >({
          nzTitle: this.getConsultationTitle(params.props.consultation),
          nzWidth: '70vw',
          nzContent: CustomerConsultationComponent,
          nzContentParams: {
            consultation: params.props.consultation
          },
          nzClosable: false,
          nzZIndex: 1000
        });

        break;
      }

      case DrawerType.PRESCRIPTION_PDF: {
        drawerRef = this.drawerService.create<
          PrescriptionPdfComponent,
          { rxId: string },
          string
        >({
          nzTitle: this.getRxDetailTitle(params.props.rx),
          nzWidth: '70vw',
          nzContent: PrescriptionPdfComponent,
          nzContentParams: {
            rxId: params.props.rxId
          },
          nzZIndex: 1000
        });
        break;
      }
      case DrawerType.CUSTOMER_PRESCRIPTIONS: {
        drawerRef = this.drawerService.create<
          CustomerPrescriptionsComponent,
          { customer: ICustomerUser },
          string
        >({
          nzTitle: 'Prescriptions',
          nzWidth: '70vw',
          nzContent: CustomerPrescriptionsComponent,
          nzContentParams: {
            customer: params.props.customer
          },
          nzZIndex: 1000
        });
        break;
      }
      case DrawerType.LAB_REQ_DETAIL: {
        drawerRef = this.drawerService.create<
          LabRequisitionDetailComponent,
          { customer: ICustomerUser; templateId: string; labReq: ILabReq },
          string
        >({
          nzTitle: params.props.labReq ? 'Edit a Lab Req' : 'Create a Lab Req',
          nzWidth: '70vw',
          nzContent: LabRequisitionDetailComponent,
          nzContentParams: {
            templateId: params.props.templateId
          },
          nzZIndex: 1000
        });
        break;
      }
      case DrawerType.LAB_REQ_DETAIL_V2: {
        drawerRef = this.drawerService.create<
          LabRequisitionDetailV2Component,
          { template: any; mode: 'edit' | 'create' },
          string
        >({
          nzTitle:
            params.props.mode === 'edit'
              ? 'Edit a Lab Req'
              : 'Create a Lab Req',
          nzWidth: '100%',
          nzWrapClassName: 'lab-req-drawer',
          nzContent: LabRequisitionDetailV2Component,
          nzContentParams: {
            template: params.props.template,
            mode: params.props.mode
          },
          nzZIndex: 1000
        });

        break;
      }
      case DrawerType.CUSTOMER_CHAT: {
        drawerRef = this.drawerService.create<
          ChatMainComponent,
          { drawerRef: NzDrawerRef },
          string
        >({
          // nzTitle: 'Chat',
          nzMaskClosable: true,
          nzWidth: '50vw',
          nzMask: true,
          nzBodyStyle: {
            padding: 0
          },
          nzContent: ChatMainComponent,
          // Above ORDER_DETAILS (1000) so chat opened from the order drawer
          // stacks on top instead of opening behind it / under its mask.
          nzZIndex: 1100
        });

        break;
      }
      case DrawerType.SCHEDULE_EDITOR: {
        drawerRef = this.drawerService.create<
          ScheduleComponent,
          { drawerRef: NzDrawerRef },
          string
        >({
          nzTitle: 'Schedule',
          nzWidth: '70vw',
          nzMask: false,

          nzContent: ScheduleComponent,
          nzZIndex: 1000
        });

        break;
      }
      case DrawerType.INVOICE_PREVIEW: {
        drawerRef = this.drawerService.create<
          InvoicePreviewComponent,
          { drawerRef: NzDrawerRef },
          string
        >({
          nzTitle: 'Invoice',
          nzWidth: '70vw',
          nzMask: false,
          nzContent: InvoicePreviewComponent,
          nzContentParams: {
            invoice: params.props.invoice
          },
          nzZIndex: 1000
        });

        break;
      }
      case DrawerType.CREATE_PATIENT: {
        drawerRef = this.drawerService.create<
          CustomerCreateComponent,
          { drawerRef: NzDrawerRef },
          string
        >({
          nzTitle: 'Create Patient',
          nzWidth: '40vw',
          nzMask: true,
          nzContent: CustomerCreateComponent,
          nzContentParams: {
            lead: params.props.lead,
            goToCustomer: params.props.goToCustomer
          },
          nzZIndex: 1000
        });

        break;
      }
      case DrawerType.APPOINTMENT_DETAILS: {
        drawerRef = this.drawerService.create<
          AppointmentDetailsComponent,
          { drawerRef: NzDrawerRef },
          string
        >({
          nzTitle: 'Appointment Details',
          nzWidth: '70vw',
          nzMask: true,
          nzContent: AppointmentDetailsComponent,
          nzContentParams: {
            appointment: params.props.appointment
          },
          nzZIndex: 1000
        });
        break;
      }
      case DrawerType.ORDER_DETAILS: {
        drawerRef = this.drawerService.create<
          OrderDetailsComponent,
          { drawerRef: NzDrawerRef },
          string
        >({
          // nzTitle: 'Order Details',
          nzWidth: '70vw',
          nzClosable: false,
          nzContent: OrderDetailsComponent,
          nzContentParams: {
            orderId: params.props.orderId
          },
          nzZIndex: 1000
        });
        break;
      }
      case DrawerType.SUBSCRIPTION_DETAILS: {
        drawerRef = this.drawerService.create<
          SubscriptionDetailsComponent,
          { drawerRef: NzDrawerRef },
          string
        >({
          nzWidth: 'fit-content',
          nzClosable: false,
          nzContent: SubscriptionDetailsComponent,
          nzContentParams: {
            subscriptionId: params.props.subscriptionId
          },
          nzZIndex: 1000
        });
        break;
      }
      case DrawerType.CREATE_SUBSCRIPTION: {
        if (!params.props?.customerId) {
          this.notificationService.error(
            'Oops!',
            'Something went wrong, refresh and try again.'
          );
          console.error(
            'Create Subscription Drawer failed to open because params is missing required fields',
            params.props
          );
          return;
        }

        drawerRef = this.drawerService.create<
          CreateSubscriptionComponent,
          { customerId: string },
          string
        >({
          nzTitle: 'Create subscription',
          nzWidth: '520px',
          nzClosable: false,
          nzContent: CreateSubscriptionComponent,
          nzContentParams: {
            customerId: params.props.customerId
          },
          nzZIndex: 1000
        });
        break;
      }
      case DrawerType.WATCHLIST_MANAGER: {
        drawerRef = this.drawerService.create<
          WatchlistManagerComponent,
          { drawerRef: NzDrawerRef },
          string
        >({
          nzTitle: 'Manage Patient Watchlist',
          nzWidth: '520px',
          nzClosable: false,
          nzContent: WatchlistManagerComponent,
          nzContentParams: {
            customer: params.props.customer
          },
          nzZIndex: 1000
        });
        break;
      }
      case DrawerType.CUSTOMER_ACTIVITY: {
        drawerRef = this.drawerService.create<
          CustomerActivityComponent,
          { drawerRef: NzDrawerRef },
          string
        >({
          nzTitle: 'Customer Activity',
          nzWidth: '40vw',
          nzClosable: true,
          nzContent: CustomerActivityComponent,
          nzContentParams: {
            customerId: params.props.customerId
          },
          nzZIndex: 1000
        });
        break;
      }
      case DrawerType.CREATE_ACTIVITY: {
        drawerRef = this.drawerService.create<
          CreateActivityComponent,
          { drawerRef: NzDrawerRef },
          string
        >({
          nzTitle: 'Create Activity',
          nzWidth: '40vw',
          nzClosable: false,
          nzContent: CreateActivityComponent,
          nzContentParams: {
            type: params.props.type,
            customerId: params.props.customerId,
            resourceId: params.props.resourceId,
            resourceType: params.props.resourceType
          },
          nzZIndex: 1000
        });
        break;
      }
      case DrawerType.VIEW_ALL_CONSULTATIONS: {
        drawerRef = this.drawerService.create<
          AllConsultationsViewComponent,
          { requirements: any[]; customer: any; orderId: number },
          string
        >({
          nzWidth: '80vw',
          nzClosable: false,
          nzContent: AllConsultationsViewComponent,
          nzContentParams: {
            requirements: params.props.requirements,
            customer: params.props.customer,
            orderId: params.props.orderId
          },
          nzZIndex: 1000
        });
        break;
      }
      case DrawerType.CANNED_MESSAGES: {
        drawerRef = this.drawerService.create<
          CannedMessagesComponent,
          { drawerRef: NzDrawerRef },
          string
        >({
          nzTitle: 'Canned Messages',
          nzWidth: '40vw',
          nzClosable: true,
          nzContent: CannedMessagesComponent,
          // Let the component's own cdkScrollable region handle scrolling so
          // nz-select dropdowns reposition/stick to their trigger on scroll.
          nzBodyStyle: { overflow: 'hidden' }
        });
        break;
      }
    }

    this.drawerRef = drawerRef;

    if (drawerRef) {
      const openedDrawersCount = Object.keys(this.openDrawerRefs).length;

      let previousDrawer = null;
      if (openedDrawersCount >= 1) {
        // indent the previous drawer
        previousDrawer = Object.entries(this.openDrawerRefs).find(
          ([key, drawer]) => drawer.index === openedDrawersCount - 1
        )[1];
      }
      this.openDrawerRefs[params.name] = {
        ref: drawerRef,
        index: Object.keys(this.openDrawerRefs).length
      };

      drawerRef.afterOpen.subscribe(() => {
        this.drawerFacade.openDrawerSuccess(params.name);
        previousDrawer && (previousDrawer.ref.nzOffsetX = 150);

        if (params.name == DrawerType.OPEN_CONSULTATION) {
          this.commonFacade.setEditMode(false);
        }
        if (params.name == DrawerType.CREATE_CONSULTATION) {
          this.commonFacade.setEditMode(true);
        }
      });

      //fulfillment drawer uses result from find-order component
      drawerRef.afterClose.subscribe(result => {
        const closeParams: DrawerParms = {
          name: params.name,
          props: result,
          onClose: params.onClose
        };
        this.drawerFacade.closeDrawer(closeParams);
        delete this.openDrawerRefs[params.name];

        previousDrawer && (previousDrawer.ref.nzOffsetX = 0);

        if (params.name == DrawerType.OPEN_CONSULTATION) {
          this.commonFacade.setEditMode(true);
        }
      });
    }
    return drawerRef;
  }

  closeDrawer(drawerName?: string) {
    if (drawerName) {
      this.openDrawerRefs[drawerName]?.ref.close(drawerName);
    } else if (this.drawerRef) {
      this.drawerRef.close();
      this.drawerRef = null;
    }
  }
}
